import { useEffect, useState } from 'react';
import { initTracker, trackDescriptionInteraction } from 'documents-lib/js/tracking/tracker';
import DescriptionFeedback from 'documents-ui/components/description/DescriptionFeedback';
import AILoadingIconButton from 'ai-components-ui-library/components/buttons/AILoadingIconButton';
import { AITextArea } from 'ai-components-ui-library/components/inputs/AITextArea';
import { Button } from 'foundations-components/transitional/components/Button';
import { Flex } from 'foundations-components/components/Flex';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CharacterCounter } from 'foundations-components/transitional/components/CharacterCounter';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DocumentDescriptionModal = ({
  correlationId,
  description,
  feedbackDisabled,
  isLoading,
  isUserEdited,
  closeModal,
  generateDescription,
  saveDescription,
  submitDescriptionFeedback
}) => {
  const [tempDescription, setTempDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [userEdited, setUserEdited] = useState(isUserEdited);
  const [isRegenerate, setIsRegenerate] = useState(!!description);
  useEffect(() => {
    setTempDescription(description);
    setUserEdited(isUserEdited);
  }, [description, isUserEdited]);
  const handleCloseModal = () => {
    setTempDescription(description);
    setUserEdited(isUserEdited);
    closeModal();
  };
  const handleOnGenerate = () => {
    setTempDescription('');
    setHasError(false);
    setUserEdited(false);
    generateDescription().then(result => {
      if (result) {
        setTempDescription(result);
      } else {
        setHasError(true);
        setTempDescription(description); // fallback to prev saved description
      }
      setIsRegenerate(true);
    }).catch(err => {
      console.error(err);
      setHasError(true);
    });
    trackDescriptionInteraction({
      action: isRegenerate ? 'regenerate-description' : 'generate-description'
    });
  };
  const handleOnSave = () => {
    setIsSaving(true);
    setHasError(false);
    saveDescription(tempDescription, userEdited).then(result => {
      setIsSaving(false);
      if (result !== null) {
        closeModal();
      }
    }).catch(err => {
      console.error(err);
      setIsSaving(false);
    });
  };
  const renderGenerateButtonText = () => {
    return isRegenerate ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "summary.description.regenerateDescription"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "summary.description.generateDescription"
    });
  };
  const renderError = () => {
    return /*#__PURE__*/_jsx(View, {
      marginBlockStart: "200",
      as: "div",
      children: /*#__PURE__*/_jsx(Text, {
        as: "small",
        color: "text-alert-default",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "summary.description.generationError"
        })
      })
    });
  };
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "document-description-modal",
    width: 600,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        color: "text-core-on-fill-default",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "summary.description.title"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: handleCloseModal
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [/*#__PURE__*/_jsxs(View, {
        marginBlockEnd: "300",
        as: "div",
        children: [/*#__PURE__*/_jsxs(View, {
          marginBlockEnd: "200",
          as: "div",
          children: [/*#__PURE__*/_jsx(FormLabel, {
            htmlFor: "description-textarea",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "summary.description.textarea.label"
            })
          }), /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(Text, {
              as: "small",
              color: "text-core-subtle",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "summary.description.textarea.labelDescription"
              })
            })
          })]
        }), /*#__PURE__*/_jsx(CharacterCounter, {
          counter: "up",
          maxLength: 500,
          value: tempDescription,
          label: ({
            count
          }) => /*#__PURE__*/_jsx(_Fragment, {
            children: /*#__PURE__*/_jsx(Text, {
              as: "small",
              color: "icon-core-default",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "summary.description.textarea.counter",
                options: {
                  count
                }
              })
            })
          }),
          renderInput: () => /*#__PURE__*/_jsx(AITextArea, {
            id: "description-textarea",
            rows: 9,
            maxLength: 500,
            value: tempDescription,
            loading: isLoading,
            disabled: isSaving,
            triggerButton: /*#__PURE__*/_jsx(AILoadingIconButton, {
              "data-test-id": "generate-description-button",
              use: "transparent",
              size: "xs",
              disabled: isSaving || isLoading,
              isRegenerate: isRegenerate,
              tooltip: renderGenerateButtonText(),
              onClick: handleOnGenerate,
              usageTracker: initTracker()
            }),
            onChange: ({
              target
            }) => {
              setTempDescription(target.value);
              setUserEdited(true);
            }
          }),
          marginBlockEnd: "200"
        })]
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsx("div", {
          style: {
            visibility: tempDescription ? 'visible' : 'hidden'
          },
          children: /*#__PURE__*/_jsx(Text, {
            as: "small",
            color: "text-core-subtle",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "summary.description.checkResults"
            })
          })
        }), /*#__PURE__*/_jsx(DescriptionFeedback, {
          correlationId: correlationId,
          feedbackDisabled: feedbackDisabled,
          description: tempDescription,
          isSaving: isSaving,
          isLoading: isLoading,
          submitDescriptionFeedback: submitDescriptionFeedback
        })]
      }), hasError && renderError()]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Button, {
        "data-test-id": "save-description-button",
        size: "small",
        use: "primary",
        disabled: isSaving || isLoading,
        onClick: handleOnSave,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "summary.description.save"
        })
      }), /*#__PURE__*/_jsx(Button, {
        size: "small",
        use: "secondary",
        onClick: handleCloseModal,
        disabled: isSaving,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "summary.description.cancel"
        })
      })]
    })]
  });
};
export default DocumentDescriptionModal;